/* eslint-disable no-console */
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { useUserStore } from '@/stores/userStore'
import { ENV } from '@/globals/typescript/utils/host'

function startDevLogListener(): (() => void) | null {
  const userStore = useUserStore()

  if (!userStore.isLabAdminUser) {
    return null
  }

  const userID = userStore.currentUser?.id
  if (!userID) {
    console.warn('[DevLogListener] No current user — cannot start listener.')
    return null
  }

  console.log('[DevLogListener] Starting log listener...')

  const startTime = Date.now()

  const q = query(
    collection(db, 'Logs'),
    where('actor.userID', '==', userID),
    where('timestamps.createdAt', '>=', startTime)
  )

  const unsubscribe = onSnapshot(q, (snapshot) => {
    snapshot.docChanges().forEach((change) => {
      if (change.type !== 'added') return

      const log = change.doc.data()
      const action: string = log.action ?? '?'
      const resourceType: string = log.resourceType ?? log.type ?? '?'
      const status: string = log.status ?? '?'
      const changedPaths: string[] | undefined = log.changedPaths
      const changes:
        | { path: string; from: unknown; to: unknown }[]
        | undefined = log.changes

      const label = `[LOG] ${action.toUpperCase()} ${resourceType}`

      const groupFn =
        status === 'failure' ? console.group : console.groupCollapsed
      groupFn(
        `%c${label}`,
        status === 'failure'
          ? 'color: #f87171; font-weight: bold'
          : 'color: #60a5fa'
      )

      if (action === 'create') {
        console.table(log.afterData)
      } else if (changes?.length) {
        console.table(changes)
      } else if (changedPaths?.length) {
        console.log('Changed paths:', changedPaths.join(', '))
      }

      // Full object
      console.log(log)

      console.groupEnd()
    })
  })

  return unsubscribe
}

const STORAGE_KEY = 'devLogListener:enabled'

/**
 * Dev-only utility — registers a global `logs()` toggle in the browser console.
 * Calling logs() starts the listener; calling it again stops it.
 * The on/off state is persisted in localStorage so it survives page reloads.
 * Only Milva staff (isLabAdminUser) can activate it.
 * Call this once on app bootstrap.
 * No-ops on prod. Gated on the runtime environment, not the build mode: one build
 * travels between environments, so the build mode says nothing about where it ran.
 */
export function initDevLogCommand(): void {
  if (!ENV.isDev) {
    return
  }

  let unsubscribe: (() => void) | null = null

  const start = (): void => {
    const userStore = useUserStore()

    if (!userStore.isLabAdminUser) {
      console.warn(
        '[DevLogListener] Access denied — only available for Milva staff.'
      )
      return
    }

    unsubscribe = startDevLogListener()
    if (unsubscribe) {
      localStorage.setItem(STORAGE_KEY, '1')
    }
  }

  const stop = (): void => {
    unsubscribe?.()
    unsubscribe = null
    localStorage.removeItem(STORAGE_KEY)
    console.log('[DevLogListener] Stopped.')
  }

  ;(window as unknown as Record<string, unknown>).logs = () => {
    if (unsubscribe) {
      stop()
    } else {
      start()
    }
  }

  // Auto-start if it was enabled before the last reload.
  // The user store isn't populated yet at bootstrap, so wait until it is.
  if (localStorage.getItem(STORAGE_KEY)) {
    const userStore = useUserStore()
    if (userStore.isLabAdminUser) {
      start()
    } else {
      const unwatch = userStore.$subscribe((_, state) => {
        if (state.isLabAdminUser) {
          unwatch()
          start()
        }
      })
    }
  } else {
    console.log('[DevLogListener] Ready — type logs() to start.')
  }
}

